import { StateCreator, create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { Menu } from '../models/menu.model';

export type MenuStoreSlice = {
  isLoading: boolean;
  menu: Menu[];
  setIsLoading: (isLoading: boolean) => void;
  setMenu: (menu: Menu[]) => void;
};

export const DefaultMenuStore = {
  isLoading: false,
  menu: [],
};

const menuStateSlice: StateCreator<
  MenuStoreSlice,
  [['zustand/devtools', never], ['zustand/persist', unknown]],
  [],
  MenuStoreSlice
> = (set) => ({
  ...DefaultMenuStore,
  setIsLoading: (isLoading: boolean) =>
    set(() => ({ isLoading }), undefined, 'menu/setIsLoading'),
  setMenu: (menu: Menu[]) =>
    set(
      () => ({ menu: Array.isArray(menu) ? menu : [] }),
      undefined,
      'menu/setMenu'
    ),
});

// Bumping the persist `version` invalidates the cached menu in every
// existing browser once this build ships. Zustand drops the persisted
// blob when the stored version doesn't match, so the sidebar starts
// blank on first mount and the app immediately refetches /menu/user
// with the fresh menu_order coming from the server. Increment when a
// server-side menu reorder / permission catalog change must reach
// clients that already had a stale cache written to localStorage.
// v3 (2026-09-18) — bumped after non-array `menu` values leaked into
// persisted storage and blew up `for-of` + `.filter` consumers. New
// stores always initialize to `[]`; setter + hydrator normalize to
// array (see below).
export const MENU_STORE_VERSION = 3;

export const useMenuStore = create<MenuStoreSlice>()(
  devtools(
    persist(menuStateSlice, {
      name: 'menu',
      version: MENU_STORE_VERSION,
      // Belt-and-suspenders — even at the current version, coerce a
      // persisted `menu` that isn't an array back to `[]`. Version
      // bump alone can't help clients whose cache landed corrupt
      // AFTER the bump (partial hydration, tab-open across builds).
      merge: (persisted, current) => {
        const p = (persisted as Partial<MenuStoreSlice> | null) ?? {};
        return {
          ...current,
          ...p,
          menu: Array.isArray(p.menu) ? p.menu : [],
        };
      },
    }),
    { name: 'menu' }
  )
);
